<template>
    <div>
        <b-table
            striped
            hover
            :items="items"
            :fields="fields"
            show-empty
            :current-page="currentPage"
            :per-page="CONSTANTS.ITEM_PER_PAGE"
        >
            <template #cell(index)="data">{{
                (currentPage - 1) * CONSTANTS.ITEM_PER_PAGE + (data.index + 1)
            }}</template>
            <template v-slot:cell(userId)="row">
                <span>{{ userName[row.value] }}</span>
            </template>
            <template v-slot:cell(status)="row">
                <b-badge
                    class="px-2 py-1 font-12"
                    :variant="
                        row.item.status === CONSTANTS.NEWS_STATUS[1].value ? 'success' : 'secondary'
                    "
                    >{{ mapStatus[row.item.status] }}</b-badge
                >
            </template>
            <template #cell(date)="data">
                <span>{{ data.value | formatDateTime }}</span>
            </template>
            <template #cell(actions)="row">
                <b-button
                    variant="primary"
                    class="mr-1 min-width-none"
                    @click="showUpdateModal(row.item.id)"
                    v-b-tooltip.hover
                    :title="$t('common.edit')"
                >
                    <i class="fa fa-pencil"></i>
                </b-button>
                <b-button
                    variant="danger"
                    @click="confirmDelete(row.item.id)"
                    v-b-tooltip.hover
                    class="min-width-none"
                    :title="$t('common.delete')"
                >
                    <i class="fa fa-trash"></i>
                </b-button>
            </template>
            <template v-slot:empty>
                <div class="text-center">
                    <span>Không có bản ghi nào</span>
                </div>
            </template>
        </b-table>
        <div v-if="countRow > CONSTANTS.ITEM_PER_PAGE" class="my-1">
            <b-pagination
                v-model="currentPage"
                :total-rows="countRow"
                :per-page="CONSTANTS.ITEM_PER_PAGE"
                :hide-goto-end-buttons="true"
                :prev-text="$t('common.prevPage')"
                :next-text="$t('common.nextPage')"
                class="my-0"
                align="center"
            />
        </div>

        <!-- Create/Edit Info -->
        <b-modal
            ref="createOrUpdateModal"
            :ok-title="`${itemInfo.id ? $t('common.update') : $t('common.create')}`"
            :title="`${itemInfo.id ? 'Cập nhật tin tức' : 'Tạo mới tin tức'}`"
            :cancel-title="$t('common.cancel')"
            @ok="handleCreateOk"
            no-close-on-esc
            no-close-on-backdrop
            size="xl"
        >
            <b-form-group :label="$t('common.name')">
                <b-form-textarea
                    maxlength="255"
                    v-model="itemInfo.name"
                    v-validate="'required'"
                    :placeholder="$t('common.name')"
                    name="name"
                    rows="3"
                    :data-vv-as="$t('common.name')"
                />
                <div v-show="errors.has('name')" class="validation-message text-danger">
                    Tên bài viết là trường bắt buộc
                </div>
            </b-form-group>
            <b-form-group label="Mô tả ngắn gọn">
                <VueEditor
                    :value="itemInfo.sapo"
                    :placeholder="'Mô tả ngắn gọn'"
                    @change="(data) => (itemInfo.sapo = data)"
                />
            </b-form-group>
            <b-form-group label="Nguồn bài viết/tác giả">
                <b-form-input
                    maxlength="255"
                    v-model="itemInfo.author"
                    placeholder="Nguồn bài viết/tác giả"
                    name="author"
                />
            </b-form-group>
            <b-form-group label="Ảnh nền">
                <div
                    v-if="itemInfo.avatar"
                    class="text-primary cursor-pointer"
                    @click="openFile(itemInfo.avatar)"
                >
                    <i class="fa fa-picture-o mr-2" />{{ displayFile(itemInfo.avatar) }}
                </div>
                <b-form-file
                    v-else
                    v-model="selectedAvatar"
                    accept="image/*"
                    placeholder="Chọn ảnh nền tải lên"
                    browse-text="Chọn tệp"
                />
            </b-form-group>
            <b-form-group label="Tệp đính kèm">
                <div v-if="itemInfo.files && itemInfo.files.length" class="cursor-pointer">
                    <div
                        v-for="file in itemInfo.files"
                        @click="openFile(file)"
                        :class="
                            file.includes('.pdf')
                                ? 'text-danger'
                                : file.includes('.doc')
                                ? 'text-info'
                                : 'text-primary'
                        "
                    >
                        <i
                            :class="`fa ${
                                file.includes('.pdf')
                                    ? 'fa-file-pdf-o'
                                    : file.includes('.doc')
                                    ? 'fa-file-word-o'
                                    : 'fa-picture-o'
                            } mr-2`"
                        />{{ displayFile(file) }}
                    </div>
                </div>
                <b-form-file
                    v-else
                    v-model="listSelectedFiles"
                    accept=".pdf, image/*, .doc, .docx"
                    placeholder="Chọn văn bản tải lên"
                    browse-text="Chọn tệp"
                    multiple
                />
            </b-form-group>
            <b-form-group label="Nội dung bài viết">
                <VueEditor
                    :value="itemInfo.content"
                    :placeholder="'Nội dung'"
                    @change="(data) => (itemInfo.content = data)"
                />
            </b-form-group>
            <b-form-group label="Ngày xuất bản">
                <date-picker
                    v-model="itemInfo.date"
                    value-type="format"
                    :format="'DD/MM/YYYY HH:mm:ss'"
                    lang="en"
                    type="datetime"
                    input-class="form-control bg-white"
                    :editable="false"
                    :clearable="false"
                    placeholder="Chọn thời gian"
                    showSecond
                />
            </b-form-group>
            <div v-if="itemInfo.type != 'DOCUMENT'">
                <b-form-group label="Trạng thái">
                    <b-form-radio-group
                        v-model="itemInfo.status"
                        :options="CONSTANTS.NEWS_STATUS"
                        name="radio-status"
                    />
                </b-form-group>
                <b-form-group label="Kiểu tin tức">
                    <b-form-radio-group
                        v-model="itemInfo.type"
                        :options="CONSTANTS.NEWS_TYPE"
                        name="radio-type"
                    />
                </b-form-group>
            </div>
        </b-modal>

        <!-- Confirm Delete -->
        <b-modal
            ref="deleteModal"
            :title="$t('common.confirmation')"
            no-close-on-esc
            no-close-on-backdrop
            :ok-title="$t('common.delete')"
            :cancel-title="$t('common.cancel')"
            ok-variant="danger"
            @ok="onDelete"
            >Bạn có chắc chắn muốn xoá tin tức này?</b-modal
        >
    </div>
</template>
<script>
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import { mapActions, mapState } from 'vuex';
import CONSTANTS from '@/constants';
import VueEditor from '@/components/VueEditor';
import API_CONFIG from '@/api-config';
export default {
    props: ['items'],
    components: { VueEditor, DatePicker },
    computed: {
        ...mapState({
            item: (state) => state.news.item,
            listUsers: (state) => state.user.users
        }),
        countRow() {
            return this.items.length;
        },
        userName() {
            const user = {};
            this.listUsers.forEach((element) => {
                user[element.id] = element.name;
            });
            return user;
        },
        mapStatus() {
            const status = {};
            CONSTANTS.NEWS_STATUS.forEach((element) => {
                status[element.value] = element.text;
            });
            return status;
        }
    },
    data() {
        return {
            fields: [
                { tdClass: 'align-middle', key: 'index', label: '#' },
                { tdClass: 'align-middle', key: 'name', label: this.$i18n.t('common.name') },
                { tdClass: 'align-middle', key: 'userId', label: 'Người tạo' },
                { tdClass: 'align-middle', key: 'status', label: 'Trạng thái' },
                {
                    tdClass: 'align-middle',
                    key: 'date',
                    label: this.$i18n.t('common.lastCreated')
                },
                {
                    thClass: 'fixed-actions-col',
                    key: 'actions',
                    label: this.$i18n.t('common.actions')
                }
            ],
            itemInfo: {},
            currentId: null,
            currentPage: 1,
            CONSTANTS,
            listSelectedFiles: [],
            selectedAvatar: null
        };
    },
    methods: {
        ...mapActions({
            updateOrCreate: 'news/updateOrCreate',
            deleteById: 'news/delete',
            createAuditByUser: 'user/createAuditByUser',
            getInfo: 'news/getInfo',
            uploadImage: 'news/uploadImage'
        }),
        handleCreateOk(modal) {
            modal.preventDefault();
            this.onSubmit();
        },
        onSubmit() {
            this.$validator.validateAll().then((result) => {
                if (result) {
                    this.update();
                    this.$refs.createOrUpdateModal.hide();
                    this.$toaster.success('Cập nhật tin tức thành công');
                    this.createAuditByUser({ content: 'Cập nhật tin tức' });
                }
            });
        },
        async showUpdateModal(id) {
            this.resetForm();
            await this.getInfo({ id: id });
            this.itemInfo = { ...this.item };
            if (this.itemInfo.date)
                this.itemInfo.date = this.$moment(this.itemInfo.date).format('DD/MM/YYYY HH:mm:ss');
            this.$refs.createOrUpdateModal.show();
        },
        async update() {
            let avatarRes = null;
            let listAttachFiles = [];
            if (this.selectedAvatar) {
                const formData = new FormData();
                formData.append('file', this.selectedAvatar);
                const res = await this.uploadImage(formData);
                avatarRes = res.url;
            }
            if (this.listSelectedFiles.length) {
                for (let i = 0; i < this.listSelectedFiles.length; i++) {
                    const element = this.listSelectedFiles[i];
                    const formData = new FormData();
                    formData.append('file', element);
                    const res = await this.uploadImage(formData);
                    listAttachFiles.push(res.url);
                }
            }
            this.itemInfo.avatar = avatarRes ? avatarRes : this.itemInfo.avatar;
            this.itemInfo.files = listAttachFiles.length ? listAttachFiles : this.itemInfo.files;
            this.itemInfo.date =
                this.$moment(this.itemInfo.date, 'DD/MM/YYYY HH:mm:ss')
                    .toISOString()
                    .split('.')[0] + 'Z';
            await this.updateOrCreate(this.itemInfo);
        },
        async onDelete(modal) {
            modal.preventDefault();
            await this.deleteById({ id: this.currentId });
            if (this.items.length / this.CONSTANTS.ITEM_PER_PAGE === this.currentPage - 1) {
                this.currentPage--;
            }
            this.$refs.deleteModal.hide();
            this.$toaster.success('Xoá tin tức thành công!');
            this.createAuditByUser({ content: 'Xoá tin tức' });
        },
        async confirmDelete(zoneId) {
            this.currentId = zoneId;
            this.$refs.deleteModal.show();
        },
        resetForm() {
            this.itemInfo = {
                status: CONSTANTS.NEWS_STATUS[0].value,
                type: CONSTANTS.NEWS_TYPE[0].value
            };
            this.selectedAvatar = null;
            this.listSelectedFiles = [];
            this.showDatePicker = false;
        },
        openFile(link) {
            const newTab = `${API_CONFIG.URL_GET_FILE}${link}`;
            window.open(newTab, '_blank');
        },
        displayFile(file) {
            const arr = file.split('_');
            return arr[arr.length - 1];
        }
    }
};
</script>